import React, { useState, useEffect } from "react";
import {
  Typography,
  Input,
  Button,
  Dropdown,
  message,
  Menu,
  Icon,
  Tooltip,
} from "antd";
import { useBranch } from "../../../ContextAPI/branch";
import { platformApi } from "../../../api";
import WithdrawalTable from "./WithdrawalTable";
import WithdrawalModal from "./WithdrawalModal";
import AddDepositModal from "./AddDepositModal";

const { Title } = Typography;
const { Search } = Input;
const { Item } = Menu;

const CashWithdrawal = () => {
  const { selectBranch } = useBranch();

  const [dataSource, setDataSource] = useState([]);
  const [denominationData, setDenominationData] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [openDeposit, setOpenDeposit] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [searchString, setSearchString] = useState("");

  /* ---------------- Page Size Menu ---------------- */
  const getMenu = () => {
    const menus = [10, 20, 50, 100];
    return menus.map((menu) => (
      <Item
        key={menu}
        onClick={() => {
          setLimit(menu);
          setPage(1);
        }}
      >
        {menu}
      </Item>
    ));
  };

  const menu = <Menu>{getMenu()}</Menu>;

  /* ---------------- Fetch Denominations ---------------- */
  const fetchDenominations = () => {
    if (!selectBranch?.length) return;

    platformApi
      .post("/api/payment/denominationInventory", {
        branch: selectBranch.map((b) => b.id),
      })
      .then((res) => {
        if (res.data.code === 200 && res.data.response.code === 200) {
          setDenominationData(res.data.response.data || []);
        } else {
          message.error("Failed to fetch denominations");
        }
      })
      .catch((err) => {
        console.error(err);
        message.error("Error fetching denominations");
      });
  };

  /* ---------------- Fetch Withdrawal History ---------------- */
  const fetchHistory = (branch) => {
    if (!branch?.length) return;

    setSpinning(true);

    platformApi
      .post("/api/accounts/cash-withdrawal-history", {
        branchIds: branch.map((b) => b.id),
        searchString,
        limit,
        page,
      })
      .then((res) => {
        setSpinning(false);
        if (res.data.code === 200) {
          setDataSource(res.data.data || []);
          setCount(res.data.data?.length || 0);
        } else {
          message.error("Failed to fetch history");
        }
      })
      .catch((err) => {
        setSpinning(false);
        console.error(err);
        message.error("Error fetching history");
      });
  };

  /* ---------------- Tooltip Renderer ---------------- */
  const renderDenominationTooltip = (denominations = []) => {
    const filtered = denominations
      .filter((d) => d.count > 0)
      .sort((a, b) => b.denomination - a.denomination);

    if (!filtered.length) {
      return <div>No cash available</div>;
    }

    return (
      <div style={{ minWidth: 140 }}>
        {filtered.map((d) => (
          <div
            key={d.denomination}
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <span>₹{d.denomination}</span>
            <span>{d.count}</span>
          </div>
        ))}
      </div>
    );
  };

  /* ---------------- Effects ---------------- */
  useEffect(() => {
    if (selectBranch?.length) {
      fetchHistory(selectBranch);
    }
  }, [selectBranch, page, limit, searchString]);

  useEffect(() => {
    fetchDenominations();
  }, [selectBranch]);

  /* ---------------- Add Withdrawal ---------------- */
  const handleAddWithdrawal = (payload) => {
    return platformApi
      .post("/api/accounts/cash-withdrawal", payload)
      .then((res) => {
        if (res.data.code === 200) {
          message.success("Cash withdrawal created successfully");
          fetchHistory(selectBranch);
          fetchDenominations();
          return Promise.resolve();
        }
        message.error(res.data.message || "Failed to create withdrawal");
        return Promise.reject();
      })
      .catch((err) => {
        console.error(err);
        message.error("Error creating withdrawal");
        return Promise.reject();
      });
  };

  /* ---------------- Add Exchange (Deposit) ---------------- */
  const handleAddExchange = (payload) => {
    return platformApi
      .post("/api/accounts/exchange", payload)
      .then((res) => {
        if (res.data.code === 200) {
          message.success("Denominations exchanged successfully");
          fetchHistory(selectBranch);
          fetchDenominations();
          return Promise.resolve();
        }
        message.error(res.data.message || "Failed to exchange denominations");
        return Promise.reject();
      })
      .catch((err) => {
        console.error(err);
        message.error("Error exchanging denominations");
        return Promise.reject();
      });
  };

  /* ---------------- Render ---------------- */
  return (
    <div>
      <div
  className="page-head"
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
  }}
>
  {/* ===== Left Section ===== */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      flex: 1,
      minWidth: 300,
    }}
  >
    <Title level={4} style={{ margin: 0 }}>
      Cash Withdrawals [{count}]
    </Title>

    <Dropdown overlay={menu} trigger={["click"]}>
      <Button>
        {limit} <Icon type="down" />
      </Button>
    </Dropdown>

    {/* -------- Branch Cash Buttons -------- */}
    {denominationData.map((item) => (
      <Tooltip
        key={item.branch.id}
        title={renderDenominationTooltip(item.denominations)}
        placement="bottom"
      >
        <Button
          type="dashed"
          disabled={!item.total}
          style={{
            borderStyle: "dashed",
            borderColor: "#ccc",
            color: "#333",
          }}
        >
          {item.branch.name} : ₹{item.total.toLocaleString("en-IN")}
        </Button>
      </Tooltip>
    ))}
  </div>

  {/* ===== Right Section ===== */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexShrink: 0,
    }}
  >
    <Search
      style={{ width: 100 }}
      placeholder="Search"
      onSearch={(val) => {
        setSearchString(val);
        setPage(1);
      }}
    />

    <Button
      type="primary"
      onClick={() => {
        setSelected(null);
        setOpen(true);
      }}
    >
      Add Withdrawal
    </Button>

    <Button
      type="primary"
      onClick={() => {
        setSelected(null);
        setOpenDeposit(true);
      }}
    >
      Add Deposit
    </Button>
  </div>
</div>


      <WithdrawalTable
        dataSource={dataSource}
        spinner={spinning}
        pagination={{
          current: page,
          pageSize: limit,
          total: count,
          onChange: (p) => setPage(p),
        }}
        openModal={(id) => {
          const record = dataSource.find((d) => d.id === id);
          setSelected(record);
          setOpen(true);
        }}
      />

      <WithdrawalModal
        open={open}
        close={() => setOpen(false)}
        branchOptions={selectBranch}
        emitData={handleAddWithdrawal}
        record={selected}
      />

      <AddDepositModal
        open={openDeposit}
        close={() => setOpenDeposit(false)}
        branchOptions={selectBranch}
        emitData={handleAddExchange}
      />
    </div>
  );
};

export default CashWithdrawal;
